import {
  Currency,
  Dinero,
  dinero,
  multiply,
  toDecimal,
  toSnapshot,
} from 'dinero.js';

export const IDR: Currency<number> = {
  code: 'IDR',
  base: 10,
  exponent: 0,
};

export const STOCK: Currency<number> = {
  code: 'STOCK',
  base: 10,
  exponent: 3,
};

export const STOCK_UI: Currency<number> = {
  code: 'STOCK',
  base: 10,
  exponent: 3,
};

// terms magnifier inspired by xendit articles about a story how
// they persists monetary value into DB
export const STOCK_MAGNIFIER = (STOCK.base as number) ** STOCK.exponent;

export function dineroFromFloat(value: number, currency: Currency<number>) {
  const factor = (currency.base as number) ** currency.exponent;
  const amount = Math.round(value * factor);

  return dinero({ amount, currency, scale: currency.exponent });
}

export function magnifyValue(value: number | string, type: 'idr' | 'stock') {
  const rawValue: number = typeof value === 'string' ? Number(value) : value;
  let dineroObj: Dinero<number>;
  switch (type) {
    case 'idr':
      dineroObj = dineroFromFloat(rawValue, IDR);
      break;

    case 'stock':
      dineroObj = dineroFromFloat(rawValue, STOCK);
      break;
  }

  const snapshot = toSnapshot(dineroObj);
  return {
    magnifiedValue: snapshot.amount,
    magnifier: (snapshot.currency.base as number) * snapshot.currency.exponent,
    raw: dineroObj,
  };
}

export function toDinero(value: number, type: 'stock' | 'idr'): Dinero<number> {
  let dineroObj: Dinero<number>;
  switch (type) {
    case 'idr':
      dineroObj = dineroFromFloat(value, IDR);
      break;

    case 'stock':
      dineroObj = dineroFromFloat(value, STOCK);
      break;
  }

  return dineroObj;
}

export function dineroStockMultiplyCeil(
  dineroObj: Dinero<number>,
  mutiplier: number
): number {
  const dineroMultipl = multiply(dineroObj, {
    amount: mutiplier,
    scale: STOCK.exponent,
  });

  return Math.ceil(Number(toDecimal(dineroMultipl)));
}

/**
 * Converts a magnified amount to its decimal representation.
 *
 * This is a library-agnostic utility function that performs pure mathematical conversion
 * of magnified values (amounts stored in smallest units) to decimal numbers. This abstraction
 * decouples components from Dinero.js, enabling future implementation changes without
 * widespread code modifications.
 *
 * Magnified values store currency amounts as integers by scaling them by (base ** exponent).
 * For example, with STOCK currency (base=10, exponent=3):
 * - Magnified: 1000 → Decimal: 1.0
 * - Magnified: 2500 → Decimal: 2.5
 * - Magnified: 500 → Decimal: 0.5
 *
 * Similarly for IDR (base=10, exponent=0):
 * - Magnified: 5000 → Decimal: 5000
 *
 * Implementation note: This function uses pure mathematical conversion (amount / (base ** exponent))
 * and does NOT depend on Dinero.js or any external library. This makes it completely future-proof
 * and enables easy migration to alternative implementations.
 *
 * @param amount - The magnified amount to convert (integer representing smallest currency unit)
 * @param currency - The currency definition containing base and exponent for the conversion
 * @returns The decimal representation as a number
 *
 * @example
 * // Convert STOCK magnified value to decimal
 * import { convertMagnifiedToDecimal, STOCK_UI } from '@diamond/shared/decimal-util-web';
 *
 * const orderQtyMultiple = convertMagnifiedToDecimal(1000, STOCK_UI);
 * // Returns: 1
 *
 * @example
 * // Convert custom currency
 * const customCurrency = { code: 'CUSTOM', base: 10, exponent: 2 };
 * const value = convertMagnifiedToDecimal(3050, customCurrency);
 * // Returns: 30.5 (3050 / (10^2) = 3050 / 100)
 *
 * @see https://github.com/dinerojs/dinero.js - While inspired by Dinero.js, this function
 * is completely independent and uses pure math for maximum portability
 */
export function convertMagnifiedToDecimal(
  amount: number,
  currency: Currency<number>
): number {
  const divisor = (currency.base as number) ** currency.exponent;
  return amount / divisor;
}
